import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { Timeline, TimezoneInfo, StoreStatus } from "../lib/utils";

export type OrderType = "pickup" | "delivery";

interface Taxes {
  category_code?: string;
  // US
  tax_percent?: number;
  // Canada
  gst?: number;
  pst?: number;
  id?: string;
}

// Full location object with all details
export interface LocationObject {
  id: string;
  hotelName?: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  country?: string;
  state?: string;
  city?: string;
  phoneNo?: string;
  pickupTimeline?: Timeline[];
  deliveryTimeline?: Timeline[];
  storeTimeline?: Timeline[]; 
  defaultTip?: boolean;
  distance?: string;
  timezone?: string;
  _timezoneInfo?: TimezoneInfo;
  _storeStatus?: StoreStatus;
  isSearchedAddress?: boolean;
  FlatBlock?: string;
  deliveryInstructions?: string;
  deliveryTipToPartner?: boolean;
  baseTipPercent?: number;
  tipGapPercent?: number;
  withinRadius?: number;
  minOrderAmount?: number;
  deliveryAmount?: number;
  asapDelay?: number;
  taxPercentage?: Taxes[];
  cashEnabled?: boolean;
}

// Consolidated location data for pickup or delivery
export interface LocationData {
  // Display string (address or location name)
  displayAddress: string;
  // Full location object with all details (timezone, timelines, etc.)
  locationObject: LocationObject | null;
  // Scheduling
  date: string;
  time: string;
}

// Delivery-specific location data with additional fields
export interface DeliveryLocationData extends LocationData {
  addressTag: string;
  instructions: string;
  restaurantLocation: LocationObject | null; 
}

interface DeliveryContextType {
  orderType: OrderType;
  setOrderType: React.Dispatch<React.SetStateAction<OrderType>>;

  // Consolidated pickup state
  pickup: LocationData;
  setPickup: React.Dispatch<React.SetStateAction<LocationData>>;

  // Consolidated delivery state
  delivery: DeliveryLocationData;
  setDelivery: React.Dispatch<React.SetStateAction<DeliveryLocationData>>;

  // Helper functions for common operations
  updatePickupLocation: (displayAddress: string, locationObject?: LocationObject | null) => void;
  updatePickupTime: (date: string, time: string) => void;
  updateDeliveryLocation: (displayAddress: string, locationObject?: LocationObject | null) => void;
  updateDeliveryTime: (date: string, time: string) => void;
  updateDeliveryInstructions: (instructions: string) => void;
  updateDeliveryAddressTag: (tag: string) => void;
  taxPercentage: Taxes[];
  setTaxPercentage: React.Dispatch<React.SetStateAction<Taxes[]>>;
  updateTaxesForRestaurant: (restaurantId: string, taxes: Taxes[]) => void;

  // Get current location based on order type
  getCurrentLocation: () => LocationData | DeliveryLocationData;
  getCurrentLocationObject: () => LocationObject | null;
  updateDeliveryRestaurant: (restaurant: LocationObject | null) => void;
  getCurrentRestaurant: () => LocationObject | null;
  // Legacy accessors for backward compatibility (will be deprecated)
  deliveryAddress: string;
  setDeliveryAddress: (address: string) => void;
  pickupLocation: string;
  setPickupLocation: (location: string) => void;
  deliveryTime: string;
  setDeliveryTime: (time: string) => void;
  pickupTime: string;
  setPickupTime: (time: string) => void;
  deliveryDate: string;
  setDeliveryDate: (date: string) => void;
  pickupDate: string;
  setPickupDate: (date: string) => void;
  selectedAddressTag: string;
  setSelectedAddressTag: (tag: string) => void;
  deliveryInstructions: string;
  setDeliveryInstructions: (instructions: string) => void;
}

const DeliveryContext = createContext<DeliveryContextType | undefined>(
  undefined,
);

const initialPickupState: LocationData = {
  displayAddress: "",
  locationObject: null,
  date: "today",
  time: "",
};

const initialDeliveryState: DeliveryLocationData = {
  displayAddress: "",
  locationObject: null,
  date: "today",
  time: "",
  addressTag: "",
  instructions: "",
  restaurantLocation: null,
};

const normalizeTaxes = (taxes: Taxes[] | undefined): Taxes[] =>
  taxes?.length ? taxes : [{ category_code: "default", tax_percent: 0, id: "" }];

const isSameTaxes = (a: Taxes[] | null, b: Taxes[]): boolean => {
  if (!a || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (
      left.category_code !== right.category_code ||
      left.tax_percent !== right.tax_percent ||
      left.id !== right.id
    ) {
      return false;
    }
  }
  return true;
};

export const DeliveryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [orderType, setOrderType] = useState<OrderType>("pickup");
  const [pickup, setPickup] = useState<LocationData>(initialPickupState);
    const [taxPercentage, setTaxPercentage] = useState<Taxes[]>([
      { category_code: "default", tax_percent: 0, id: "" },
    ]);
  const currentRestaurantIdRef = useRef<string | null>(null);
  const lastTaxesRef = useRef<Taxes[] | null>(null);
  const updateTaxesForRestaurant = useCallback((restaurantId: string, taxes: Taxes[]) => {
    const normalizedTaxes = normalizeTaxes(taxes);
    if (restaurantId && restaurantId === currentRestaurantIdRef.current) {
      if (isSameTaxes(lastTaxesRef.current, normalizedTaxes)) return;
    }
    currentRestaurantIdRef.current = restaurantId || null;
    lastTaxesRef.current = normalizedTaxes;
    setTaxPercentage(normalizedTaxes);
  }, []);
  const [delivery, setDelivery] = useState<DeliveryLocationData>(initialDeliveryState);
  const updateDeliveryRestaurant = (restaurant: LocationObject | null) => {
    setDelivery(prev => ({ ...prev, restaurantLocation: restaurant }));
  };
  const getCurrentRestaurant = () => {
  return orderType === "pickup" 
    ? pickup.locationObject  // For pickup, location IS the restaurant
    : delivery.restaurantLocation; // For delivery, use separate restaurant
};
  // Helper functions
  const updatePickupLocation = (displayAddress: string, locationObject?: LocationObject | null) => {
    setPickup(prev => ({
      ...prev,
      displayAddress,
      ...(locationObject !== undefined && { locationObject }),
    }));
  };

  const updatePickupTime = (date: string, time: string) => {
    setPickup(prev => ({ ...prev, date, time }));
  };

  const updateDeliveryLocation = (displayAddress: string, locationObject?: LocationObject | null) => {
    setDelivery(prev => ({
      ...prev,
      displayAddress,
      ...(locationObject !== undefined && { locationObject }),
    }));
  };

  const updateDeliveryTime = (date: string, time: string) => {
    setDelivery(prev => ({ ...prev, date, time }));
  };

  const updateDeliveryInstructions = (instructions: string) => {
    setDelivery(prev => ({ ...prev, instructions }));
  };

  const updateDeliveryAddressTag = (tag: string) => {
    setDelivery(prev => ({ ...prev, addressTag: tag }));
  };

  useEffect(() => {
    const restaurant =
      orderType === "pickup" ? pickup.locationObject : delivery.restaurantLocation;
    if (!restaurant?.id || !restaurant.taxPercentage) return;
    updateTaxesForRestaurant(restaurant.id, restaurant.taxPercentage);
  }, [orderType, pickup.locationObject, delivery.restaurantLocation, updateTaxesForRestaurant]);

  const getCurrentLocation = () => {
    return orderType === "pickup" ? pickup : delivery;
  };

  const getCurrentLocationObject = () => {
    return orderType === "pickup" ? pickup.locationObject : delivery.locationObject;
  };

  // Legacy accessors for backward compatibility
  const deliveryAddress = delivery.displayAddress;
  const setDeliveryAddress = (address: string) => updateDeliveryLocation(address);

  const pickupLocation = pickup.displayAddress;
  const setPickupLocation = (location: string) => updatePickupLocation(location);

  const deliveryTime = delivery.time;
  const setDeliveryTime = (time: string) => setDelivery(prev => ({ ...prev, time }));

  const pickupTime = pickup.time;
  const setPickupTime = (time: string) => setPickup(prev => ({ ...prev, time }));

  const deliveryDate = delivery.date;
  const setDeliveryDate = (date: string) => setDelivery(prev => ({ ...prev, date }));

  const pickupDate = pickup.date;
  const setPickupDate = (date: string) => setPickup(prev => ({ ...prev, date }));

  const selectedAddressTag = delivery.addressTag;
  const setSelectedAddressTag = (tag: string) => updateDeliveryAddressTag(tag);

  const deliveryInstructions = delivery.instructions;
  const setDeliveryInstructions = (instructions: string) => updateDeliveryInstructions(instructions);

  return (
    <DeliveryContext.Provider
      value={{
        orderType,
        setOrderType,
        pickup,
        setPickup,
        delivery,
        setDelivery,
        updatePickupLocation,
        updatePickupTime,
        updateDeliveryLocation,
        updateDeliveryTime,
        updateDeliveryInstructions,
        updateDeliveryAddressTag,
        getCurrentLocation,
        getCurrentLocationObject,
        // Legacy accessors
        deliveryAddress,
        setDeliveryAddress,
        pickupLocation,
        setPickupLocation,
        deliveryTime,
        setDeliveryTime,
        pickupTime,
        setPickupTime,
        deliveryDate,
        setDeliveryDate,
        pickupDate,
        setPickupDate,
        selectedAddressTag,
        setSelectedAddressTag,
        deliveryInstructions,
        setDeliveryInstructions,
         updateDeliveryRestaurant, 
        getCurrentRestaurant,  
        taxPercentage,
        setTaxPercentage,
        updateTaxesForRestaurant,
      }}
    >
      {children}
    </DeliveryContext.Provider>
  );
};

export const useDelivery = () => {
  const context = useContext(DeliveryContext);
  if (!context) {
    throw new Error("useDelivery must be used inside DeliveryProvider");
  }
  return context;
};
